import {
    MessageItemDto,
    ResponsesMessageRecord
} from '../api/responses/responses-types';

/**
 * Plain text of a stored user or assistant message: the text parts joined,
 * with refusals included so a declined answer can still be read back.
 * Non-message records (tool calls, reasoning, compaction markers) yield ''.
 */
export const getMessageRecordText = (
    record: ResponsesMessageRecord
): string => {
    const item = record.message;
    if (item.type !== 'message' || !('content' in item)) {
        return '';
    }

    return (item as MessageItemDto).content
        .map(part => {
            if (part.type === 'input_text' || part.type === 'output_text') {
                return part.text;
            }
            if (part.type === 'refusal') {
                return part.refusal;
            }
            return '';
        })
        .filter(Boolean)
        .join('\n')
        .trim();
};

export interface SpokenTextSlice {
    text: string;
    truncated: boolean;
    /** Offset to pass back to continue reading, present only when truncated. */
    nextOffset?: number;
    totalCharacters: number;
}

/**
 * Bounds text for speech. Long responses are read in chunks: the caller
 * passes back `nextOffset` to continue from where the previous chunk ended.
 * Cuts on a sentence or line boundary where one falls in the last quarter of
 * the chunk so the voice does not stop mid-sentence.
 */
export const sliceSpokenText = (
    text: string,
    offset = 0,
    maxCharacters = 1_500
): SpokenTextSlice => {
    const start = Math.max(0, Math.min(offset, text.length));
    const remaining = text.slice(start);
    if (remaining.length <= maxCharacters) {
        return {
            text: remaining,
            truncated: false,
            totalCharacters: text.length
        };
    }

    const window = remaining.slice(0, maxCharacters);
    const boundary = Math.max(
        window.lastIndexOf('. '),
        window.lastIndexOf('.\n'),
        window.lastIndexOf('\n')
    );
    const cut =
        boundary >= Math.floor(maxCharacters * 0.75)
            ? boundary + 1
            : maxCharacters;

    return {
        text: window.slice(0, cut).trimEnd(),
        truncated: true,
        nextOffset: start + cut,
        totalCharacters: text.length
    };
};
